import useSelectionStore from '@store/useSelectionStore';
import useGameStore from '@store/useGameStore';
import { ClickableType } from '@utils/enums';
import { cancelAction } from '@services/actionService';
import ActionIcon from '@components/ui/game/action-icon/ActionIcon';
import UnitIcon from '@components/game/objects/UnitIcon';
import PlannedActionDelete from '@components/ui/game/planned-action/PlannedActionDelete';
import APDisplay from '@components/ui/game/ap-display/APDisplay';
import EnergyDisplay from '@components/ui/game/energy-display/EnergyDisplay';
import { Target, Hash, User } from 'lucide-react';
import './PlannedActionsPanel.css';

export default function PlannedActionsPanel() {
  const { currentClick } = useSelectionStore();
  const game = useGameStore(state => state.game);

  // Only show if planned actions are selected
  if (!currentClick || currentClick.type !== ClickableType.PLANNED_ACTION) {
    return null;
  }

  // Get the actions from the click data (it's an array)
  const plannedActions = currentClick.data;
  if (!plannedActions || !Array.isArray(plannedActions)) {
    console.warn('[PlannedActionsPanel] No planned actions in currentClick.data');
    return null;
  }

  // Sort by resolution order
  const sortedActions = [...plannedActions].sort((a, b) => {
    const orderA = a.resolution_order || a.sequence_number || 999;
    const orderB = b.resolution_order || b.sequence_number || 999;
    return orderA - orderB;
  });

  if (sortedActions.length === 0) {
    return null; // Don't show panel if no planned actions
  }

  const handleDeleteAction = async (actionId) => {
    if (!game?.id) {
      console.error('[PlannedActionsPanel] No game ID available');
      return;
    }

    try {
      await cancelAction(game.id, actionId);
      console.log('[PlannedActionsPanel] Action deleted:', actionId);
    } catch (error) {
      console.error('[PlannedActionsPanel] Failed to delete action:', error);
    }
  };

  // Determine if showing single tile or multiple actions
  const isSingleTile = sortedActions.length === 1 ||
    (sortedActions.every(a => {
      const firstTarget = sortedActions[0].target_position || sortedActions[0].target_data;
      const thisTarget = a.target_position || a.target_data;
      return firstTarget && thisTarget &&
        firstTarget.x === thisTarget.x &&
        firstTarget.y === thisTarget.y;
    }));

  const title = sortedActions.length === 1
    ? "Planned Action"
    : `${sortedActions.length} Planned Actions${isSingleTile ? ' on Tile' : ''}`;

  return (
    <div className="planned-actions-panel">
      <h4 className="planned-actions-title">{title}</h4>

      <div className="planned-actions-list">
        {sortedActions.map(action => {
          const apCost = action.action?.ap_cost || 0;
          const energyCost = action.action?.energy_cost || 0;
          const targetPos = action.target_position || action.target_data;
          const resolutionOrder = action.resolution_order || action.sequence_number;

          // Get unit for this action to determine team
          const unit = useGameStore.getState().getUnitOrAvailableUnitById(action.unit_id);
          const teamClass = action.teamClass || unit?.teamClass || 'unknown';

          return (
            <div key={action.id} className="planned-action-item">
              <div className="planned-action-header">
                <div className="planned-action-info">
                  <ActionIcon
                    action={action.action}
                    size={20}
                    teamClass={teamClass}
                  />
                  <span className="planned-action-name">
                    {action.action?.name || 'Unknown Action'}
                  </span>
                  {resolutionOrder && (
                    <span className="planned-action-order" title="Resolution Order">
                      <Hash size={12} />
                      {resolutionOrder}
                    </span>
                  )}
                </div>

                {teamClass === 'own' && (
                  <PlannedActionDelete
                    action={action}
                    onDelete={() => handleDeleteAction(action.id)}
                    size={16}
                  />
                )}
              </div>

              <div className="planned-action-details">
                <div className="planned-action-unit">
                  <UnitIcon unitId={action.unit_id} size={16} />
                  <span className="unit-name">{unit?.name || `Unit ${action.unit_id}`}</span>
                </div>

                {targetPos && (
                  <div className="planned-action-target">
                    <Target size={12} />
                    <span>({targetPos.x}, {targetPos.y})</span>
                  </div>
                )}

                <div className="planned-action-costs">
                  {apCost > 0 && (
                    <APDisplay
                      baseTotal={apCost}
                      total={apCost}
                      filled={apCost}
                      size="tiny"
                    />
                  )}
                  {energyCost > 0 && (
                    <EnergyDisplay
                      value={energyCost}
                      size="tiny"
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}